import { reportingConstants as C, accountConstants } from "../constants";

const initialState = {
  // Array of reporting_widget rows, each with a nested `series` array.
  // null = not loaded yet.
  widgets: null,
  fetchingWidgets: false,
  widgetsLoaded: false,
  error: null,

  checkingReportingIntegrity: false,
  reportingIntegrity: false,
};

// ---------------------------------------------------------------------------
// Helpers (all immutable)
// ---------------------------------------------------------------------------
const withSeries = (widget) => ({ ...widget, series: widget.series || [] });

// Server responses for a widget update may not include series; keep ours.
const mergeWidget = (widget, patch) => ({ ...widget, ...patch, series: widget.series });

const updateWidgets = (state, fn) => ({ ...state, widgets: (state.widgets || []).map(fn) });

const replaceWidget = (state, patch) =>
  updateWidgets(state, (w) => (w.widget_id === patch.widget_id ? mergeWidget(w, patch) : w));

const replaceSeries = (state, patch) =>
  updateWidgets(state, (w) =>
    w.widget_id !== patch.widget_id
      ? w
      : {
          ...w,
          series: w.series.map((s) => (s.series_id === patch.series_id ? { ...s, ...patch } : s)),
        }
  );

const removeSeries = (state, seriesId) =>
  updateWidgets(state, (w) =>
    w.series.some((s) => s.series_id === seriesId)
      ? { ...w, series: w.series.filter((s) => s.series_id !== seriesId) }
      : w
  );

// Mirrors updateAccountNumberReferences on the server: series are matched by
// id, and associated rows share account_id with their owner account.
const renumberSeries = (state, renumbered) => {
  if (!state.widgets) return state;
  const assocId = renumbered.associated_account_id;
  const matches = (s) =>
    assocId != null
      ? Number(s.associated_account_id) === Number(assocId)
      : Number(s.account_id) === Number(renumbered.account_id) && Number(s.associated_account_id) === -1;

  return updateWidgets(state, (w) =>
    w.series.some(matches)
      ? { ...w, series: w.series.map((s) => (matches(s) ? { ...s, account_number: renumbered.to } : s)) }
      : w
  );
};

// Puts the series in the given order with sort_order 0..n-1, as the server
// stores them. Series missing from orderedIds keep their place at the end.
const reorderSeries = (series, orderedIds) => {
  const rank = new Map(orderedIds.map((id, index) => [id, index]));
  return [...series]
    .sort((a, b) => (rank.get(a.series_id) ?? Infinity) - (rank.get(b.series_id) ?? Infinity))
    .map((s, index) => (s.sort_order === index ? s : { ...s, sort_order: index }));
};

const insertAt = (list, index, item) => {
  const next = [...list];
  next.splice(Math.min(Math.max(index, 0), next.length), 0, item);
  return next;
};

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------
export function reporting(state = initialState, action) {
  switch (action.type) {
    // Load
    case C.GET_REPORTING_WIDGETS_REQUEST:
      return { ...state, fetchingWidgets: true, error: null };
    case C.GET_REPORTING_WIDGETS_SUCCESS:
      return {
        ...state,
        fetchingWidgets: false,
        widgetsLoaded: true,
        widgets: (action.widgets || []).map(withSeries),
      };
    case C.GET_REPORTING_WIDGETS_FAILURE:
      return { ...state, fetchingWidgets: false, error: action.error };

    // Widgets
    case C.CREATE_REPORTING_WIDGET_SUCCESS:
      return { ...state, widgets: [...(state.widgets || []), withSeries(action.widget)] };

    case C.UPDATE_REPORTING_WIDGET_REQUEST: // optimistic
    case C.UPDATE_REPORTING_WIDGET_SUCCESS:
      return replaceWidget(state, action.widget);
    case C.UPDATE_REPORTING_WIDGET_FAILURE:
      return {
        ...(action.previous ? replaceWidget(state, action.previous) : state),
        error: action.error,
      };

    case C.DELETE_REPORTING_WIDGET_REQUEST: // optimistic
      return {
        ...state,
        widgets: (state.widgets || []).filter((w) => w.widget_id !== action.widgetId),
      };
    case C.DELETE_REPORTING_WIDGET_FAILURE:
      return {
        ...state,
        widgets: action.previous
          ? insertAt(state.widgets || [], action.previous.index, action.previous.widget)
          : state.widgets,
        error: action.error,
      };

    // Series
    case C.CREATE_REPORTING_SERIES_SUCCESS:
      return updateWidgets(state, (w) =>
        w.widget_id === action.series.widget_id ? { ...w, series: [...w.series, action.series] } : w
      );
    case C.CREATE_REPORTING_SERIES_BULK_SUCCESS:
      return updateWidgets(state, (w) =>
        w.widget_id === action.widgetId ? { ...w, series: [...w.series, ...action.series] } : w
      );

    case C.UPDATE_REPORTING_SERIES_REQUEST: // optimistic
    case C.UPDATE_REPORTING_SERIES_SUCCESS:
      return replaceSeries(state, action.series);
    case C.UPDATE_REPORTING_SERIES_FAILURE:
      return {
        ...(action.previous ? replaceSeries(state, action.previous) : state),
        error: action.error,
      };

    case C.DELETE_REPORTING_SERIES_REQUEST: // optimistic
      return removeSeries(state, action.seriesId);
    case C.DELETE_REPORTING_SERIES_FAILURE: {
      const prev = action.previous;
      const restored = prev
        ? updateWidgets(state, (w) =>
            w.widget_id === prev.series.widget_id
              ? { ...w, series: insertAt(w.series, prev.index, prev.series) }
              : w
          )
        : state;
      return { ...restored, error: action.error };
    }

    case C.REORDER_REPORTING_SERIES_REQUEST: // optimistic
      return updateWidgets(state, (w) =>
        w.widget_id === action.widgetId ? { ...w, series: reorderSeries(w.series, action.orderedIds) } : w
      );
    case C.REORDER_REPORTING_SERIES_FAILURE:
      return {
        ...(action.previous
          ? updateWidgets(state, (w) => (w.widget_id === action.widgetId ? { ...w, series: action.previous } : w))
          : state),
        error: action.error,
      };

    case C.CREATE_REPORTING_WIDGET_FAILURE:
    case C.CREATE_REPORTING_SERIES_FAILURE:
    case C.CREATE_REPORTING_SERIES_BULK_FAILURE:
      return { ...state, error: action.error };

    // Integrity
    case C.IS_REPORTING_INTEGRITY_INTACT_REQUEST:
      return { ...state, checkingReportingIntegrity: true };
    case C.IS_REPORTING_INTEGRITY_INTACT_SUCCES:
      return { ...state, checkingReportingIntegrity: false, reportingIntegrity: true };
    case C.IS_REPORTING_INTEGRITY_INTACT_FAILURE:
      return { ...state, checkingReportingIntegrity: false, error: action.error };
    case C.SET_REPORTING_INTEGRITY_FALSE_SUCCESS:
      return { ...state, reportingIntegrity: false };

    // An account was renumbered; the server already updated the stored series.
    case accountConstants.UPDATE_ACCOUNTS_SUCCESS:
      return action.payload?.renumberedAccount
        ? renumberSeries(state, action.payload.renumberedAccount)
        : state;

    default:
      return state;
  }
}